import { ChevronDown, Plus } from "lucide-react";
import { StatusDot, STATUS_LABEL, statusOf } from "@/components/StatusDot";
import { cn } from "@/lib/cn";
import type { Section } from "@/lib/briefs";
import { useMenu } from "@/lib/useMenu";

const OverviewDot = () => <span className="border-status-empty inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed" />;

function Label({ section, index }: { section: Section; index: number }) {
  return (
    <span className="min-w-0 truncate">
      <span className="tabular-nums">{index + 1}</span> {section.title}
      {section.star ? (
        <span className="text-star ml-1" aria-label="Matters most">
          ★
        </span>
      ) : null}
    </span>
  );
}

/**
 * The section rail on a phone: one full-width button naming the box in view,
 * opening the same list the desktop rail shows (Overview, every box with its
 * status, Add section). How many are clear is already in the brief header
 * above it. A strip of chips was cut off mid-word with nothing to
 * say it scrolled.
 */
export function SectionPicker({
  sections,
  shownId,
  onPick,
  onAdd,
  canEdit,
}: {
  sections: Section[];
  shownId: string | null;
  onPick: (id: string | null) => void;
  onAdd: () => void;
  canEdit: boolean;
}) {
  const menu = useMenu();
  const index = sections.findIndex((s) => s.id === shownId);
  const current = index >= 0 ? sections[index] : null;
  const pick = (id: string | null) => {
    menu.close();
    onPick(id);
  };
  const item = (active: boolean) =>
    cn(
      "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
      active ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-secondary",
    );

  return (
    <div ref={menu.ref} className="relative">
      <button
        type="button"
        {...menu.buttonProps}
        aria-label={current ? `Section: ${index + 1} ${current.title}, ${STATUS_LABEL[statusOf(current)]}` : "Section: Overview"}
        className="border-input bg-card text-foreground hover:bg-accent flex h-10 w-full min-w-0 items-center gap-2 rounded-md border px-3 text-left text-sm transition-colors"
      >
        {current ? <StatusDot status={statusOf(current)} /> : <OverviewDot />}
        {current ? <Label section={current} index={index} /> : <span className="min-w-0 truncate">Overview</span>}
        <ChevronDown size={16} className={cn("text-muted-foreground ml-auto shrink-0 transition-transform", menu.open && "rotate-180")} />
      </button>
      {menu.open ? (
        <div
          role="menu"
          aria-label="Sections"
          className="bg-card border-border absolute inset-x-0 top-full z-30 mt-1 max-h-[60dvh] overflow-y-auto rounded-lg border p-1 shadow-xl"
        >
          <button type="button" role="menuitem" aria-current={shownId === null ? "true" : undefined} className={item(shownId === null)} onClick={() => pick(null)}>
            <OverviewDot />
            <span className="truncate">Overview</span>
          </button>
          {sections.map((s, i) => {
            const status = statusOf(s);
            return (
              <button
                key={s.id}
                type="button"
                role="menuitem"
                aria-current={shownId === s.id ? "true" : undefined}
                className={item(shownId === s.id)}
                onClick={() => pick(s.id)}
                title={STATUS_LABEL[status]}
              >
                <StatusDot status={status} />
                <Label section={s} index={i} />
              </button>
            );
          })}
          {canEdit ? (
            <button
              type="button"
              role="menuitem"
              className={cn(item(false), "text-muted-foreground")}
              onClick={() => {
                menu.close();
                onAdd();
              }}
            >
              <Plus size={12} className="shrink-0" />
              <span className="truncate">Add section</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
